/**
 * Minimal shape of the Electron preload clipboard bridge. Declared locally to
 * avoid a dependency on @mangovoice/types from this shared UI package.
 */
interface ElectronClipboardWindow {
  electron?: {
    clipboard?: {
      writeText: (text: string) => void
    }
  }
}

/**
 * Copies `text` to the system clipboard.
 *
 * In the Electron desktop app, `navigator.clipboard.writeText` can resolve
 * successfully without actually writing (so the async path below would report
 * success while leaving the clipboard untouched). When the native Electron
 * clipboard bridge is present, route through it first — it goes through the
 * main process and reliably writes.
 *
 * Otherwise tries the async Clipboard API. When that fails — most often because
 * the host page is running inside an Admin2 iframe without
 * `allow="clipboard-write"` on the parent frame, or in an insecure context —
 * falls back to the legacy `execCommand('copy')` flow with a hidden, selected
 * textarea. The fallback works in iframes because it relies on a user-gesture
 * synchronous copy rather than the permissioned Clipboard API.
 *
 * Throws if all paths fail so callers can surface a visible error.
 */
export async function copyToClipboard(text: string): Promise<void> {
  const electronClipboard = (window as unknown as ElectronClipboardWindow)
    .electron?.clipboard
  if (electronClipboard?.writeText) {
    // `await` is a no-op on the current void-returning bridge, but ensures we
    // don't silently drop a promise if it ever switches to ipcRenderer.invoke.
    await electronClipboard.writeText(text)
    return
  }

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text)
      return
    } catch {
      // fall through to the textarea fallback
    }
  }

  // Selecting the hidden textarea moves focus to it; removing it would leave
  // focus on <body>, so return focus to the control that triggered the copy.
  const previouslyFocused =
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null
  const textarea = document.createElement('textarea')
  textarea.value = text
  // Keep it off-screen and non-interactive; still must be in the DOM
  // for execCommand('copy') to find the selection.
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.top = '0'
  textarea.style.left = '0'
  textarea.style.opacity = '0'
  textarea.style.pointerEvents = 'none'
  document.body.appendChild(textarea)
  try {
    textarea.select()
    textarea.setSelectionRange(0, textarea.value.length)
    if (!document.execCommand('copy')) {
      throw new Error('document.execCommand("copy") returned false')
    }
  } finally {
    document.body.removeChild(textarea)
    if (previouslyFocused?.isConnected) {
      previouslyFocused.focus({ preventScroll: true })
    }
  }
}
